import * as icons from '@dtx-company/app/shared/page/src/assets/icons'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { FC, KeyboardEvent } from 'react'
import { FIRST_MONTH_FREE } from '@dtx-company/app/shared/common/src/constants/promoCodes'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { PPEPromoCodeState } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/hooks/usePPEPromoCodeState'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { fireHeapEvent } from '@dtx-company/true-common/src/utils/heap'
import { useDebouncedCallback } from 'use-debounce'
import { Button, Stack, TextField, Typography } from '@dtx-company/design-system/src'

export type PromoCodeInputProps = {
  promoCode: string
  setPromoCode: (promoCode: string) => void
  onApply: (promoCode: string) => Promise<void>
} & Pick<PPEPromoCodeState, 'state'>

const PromoCodeInput: FC<PromoCodeInputProps> = ({ promoCode, setPromoCode, state, onApply }) => {
  /** Validate promoCode text when the user presses the Enter key in the field */
  function onInputKeyPress(event: KeyboardEvent<HTMLInputElement>): void {
    const target = event.target as HTMLInputElement // For some reason TS can't deduce event.target is for HTMLInputElement
    if (event.key === 'Enter') onApply(target.value)
  }

  const trackPromoCodeChanges = useDebouncedCallback((promoText: string) => {
    fireHeapEvent('Revenue_Upgrade_Changed_PromoCode', { promoText })
  }, 300)

  const _PromoHelperText = (): JSX.Element | null => {
    if (state === 'invalid')
      return (
        <Typography variant="bodySmall" color="error" sx={{ mt: 1 }}>
          Invalid promo code
        </Typography>
      )

    if (state === 'valid') {
      if (promoCode === FIRST_MONTH_FREE) {
        return <FirstMonthFreeCouponApplied />
      }

      return (
        <Typography variant="bodySmall" color="success.main" sx={{ mt: 1 }}>
          Discount applied
        </Typography>
      )
    }
    return null
  }

  const applyBtnDisabled = state !== 'initial'
  return (
    <Stack data-testid="promo-code-input">
      <TextField
        label="Enter promotion code"
        labelAbove
        fullWidth
        value={promoCode}
        placeholder="Promotioncode10"
        type="text"
        error={state === 'invalid'}
        disabled={state === 'validating'}
        startAdornment={<Icon height={20} width={40} alt="" src={icons.coupon} />}
        endAdornment={
          <Button
            type="button"
            size="small"
            disabled={applyBtnDisabled}
            onClick={() => {
              // We don't need an onClick because clicking this button would blur
              // the field which would call onApply
            }}
            sx={{ minWidth: '62px', height: '32px', mr: '5px' }}
          >
            {state === 'valid' ? 'Applied' : 'Apply'}
          </Button>
        }
        inputProps={{
          onKeyPress: onInputKeyPress
        }}
        onChange={e => {
          trackPromoCodeChanges(e.target.value)
          setPromoCode(e.target.value)
        }}
        onFocus={() => {
          fireHeapEvent('Revenue_Upgrade_Focused_PromoCodeTextField')
        }}
        onBlur={event => {
          onApply(event.target.value)
        }}
        helperText={<_PromoHelperText />}
      />
    </Stack>
  )
}

export default PromoCodeInput

const FirstMonthFreeCouponApplied = (): JSX.Element => (
  <Box
    backgroundImage="url('/images/green-tag.png')"
    backgroundSize="contain"
    backgroundRepeat="no-repeat"
    py="8px"
    px="24px"
    mt="8px"
  >
    <Text variant="body/large" color={'white'}>
      {`${FIRST_MONTH_FREE} applied`}
    </Text>
  </Box>
)
